import { useMemo, useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Button,
  TabList,
  Tab,
  Badge,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  CopyRegular,
  CheckmarkRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  AlertRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { NodeRunData } from '@/types/editorRunData';
import type { Action, Trigger } from '@/types/workflowDefinition';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { formatDuration as canonicalFormatDuration, formatTime as canonicalFormatTime } from '@/utils/dateTime';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  tabList: {
    flexShrink: 0,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingLeft: tokens.spacingHorizontalS,
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    ...customScrollbarStyles,
  },
  // Status section
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '70px',
  },
  statusValue: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  // Error section
  errorSection: {
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
  },
  errorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    fontFamily: tokens.fontFamilyMonospace,
  },
  // Data viewer
  dataViewer: {
    flex: 1,
    minHeight: 0,
  },
  dataContent: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    overflow: 'auto',
    maxHeight: '300px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    ...customScrollbarStyles,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  // Collapsible section
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    cursor: 'pointer',
    padding: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    borderRadius: tokens.borderRadiusSmall,
  },
  collapsibleTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  copyButton: {
    marginLeft: 'auto',
  },
  // Expression evaluation section
  expressionSection: {
    marginTop: tokens.spacingVerticalM,
  },
  expressionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
  },
  expressionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalXS,
  },
  expressionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  expressionKey: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  },
  expressionAuthoredLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
  expressionAuthored: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorPalettePurpleForeground2,
    backgroundColor: tokens.colorPalettePurpleBackground2,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    display: 'inline-block',
  },
  expressionArrow: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase100,
  },
  expressionResolvedLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  expressionResolved: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    overflowX: 'auto',
    maxWidth: '100%',
    display: 'block',
  },
  noExpression: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  allParamsSection: {
    marginTop: tokens.spacingVerticalL,
  },
  badgeMargin: {
    marginLeft: tokens.spacingHorizontalXS,
  },
});

type RunDataTab = 'expressions' | 'input' | 'output' | 'properties';

interface RunDataPanelProps {
  /** Node ID to show data for */
  nodeId: string;
  /** Node name for display (reserved for future use in panel title) */
  nodeName?: string;
}

/**
 * Format duration in human-readable format
 */
function formatDuration(ms: number | undefined): string {
  return canonicalFormatDuration(ms, { style: 'execution', fallback: '-' });
}

/**
 * Format timestamp for display
 */
function formatTime(isoString: string | undefined): string {
  return canonicalFormatTime(isoString, { seconds: true, fallback: '-' });
}

/**
 * Get status badge appearance
 */
function getStatusBadge(status: NodeRunData['status']): { color: 'success' | 'danger' | 'warning' | 'informative'; label: string } {
  switch (status) {
    case 'succeeded':
      return { color: 'success', label: 'Succeeded' };
    case 'failed':
      return { color: 'danger', label: 'Failed' };
    case 'running':
      return { color: 'informative', label: 'Running' };
    case 'skipped':
      return { color: 'warning', label: 'Skipped' };
    case 'pending':
    default:
      return { color: 'informative', label: 'Pending' };
  }
}

/**
 * Check if a string contains an expression
 */
function containsExpression(value: string): boolean {
  return value.includes('${') || value.includes('@{') ||
    value.includes('@outputs') || value.includes('@body') ||
    value.includes('@parameters') || value.includes('@variables') || value.includes('@trigger') ||
    value.includes('@item') || value.includes('@concat') || value.includes('@if');
}

/**
 * Extract authored expressions from node definition and match with resolved values
 */
interface ExpressionMapping {
  paramName: string;
  authoredValue: string;
  resolvedValue: unknown;
  hasExpression: boolean;
}

function extractExpressionMappings(
  nodeDefinition: Action | Trigger | undefined,
  runtimeInput: unknown
): ExpressionMapping[] {
  const mappings: ExpressionMapping[] = [];

  if (!nodeDefinition?.inputs || typeof nodeDefinition.inputs !== 'object') {
    return mappings;
  }

  const inputs = nodeDefinition.inputs as Record<string, unknown>;
  const runtimeInputs = (runtimeInput && typeof runtimeInput === 'object')
    ? runtimeInput as Record<string, unknown>
    : {};

  // Recursively extract expressions from inputs
  function extractFromObject(
    obj: Record<string, unknown>,
    runtime: Record<string, unknown>,
    prefix: string = ''
  ) {
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const runtimeValue = runtime[key];

      if (typeof value === 'string') {
        const hasExpr = containsExpression(value);
        mappings.push({
          paramName: fullKey,
          authoredValue: value,
          resolvedValue: runtimeValue,
          hasExpression: hasExpr,
        });
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recurse into nested objects
        const nestedRuntime = (runtimeValue && typeof runtimeValue === 'object' && !Array.isArray(runtimeValue))
          ? runtimeValue as Record<string, unknown>
          : {};
        extractFromObject(value as Record<string, unknown>, nestedRuntime, fullKey);
      } else if (value !== undefined) {
        // For non-string, non-object values (numbers, booleans, arrays)
        mappings.push({
          paramName: fullKey,
          authoredValue: JSON.stringify(value),
          resolvedValue: runtimeValue,
          hasExpression: false,
        });
      }
    }
  }

  extractFromObject(inputs, runtimeInputs);

  // Sort: expressions first, then by name
  return mappings.sort((a, b) => {
    if (a.hasExpression && !b.hasExpression) return -1;
    if (!a.hasExpression && b.hasExpression) return 1;
    return a.paramName.localeCompare(b.paramName);
  });
}

/**
 * CollapsibleSection component for expandable data sections
 */
const CollapsibleSection = ({
  title,
  data,
  defaultExpanded = true,
}: {
  title: string;
  data: unknown;
  defaultExpanded?: boolean;
}) => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [data]);

  const hasData = data !== undefined && data !== null;

  return (
    <div>
      <div
        className={styles.collapsibleHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        {isExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
        <Text className={styles.collapsibleTitle}>{title}</Text>
        {hasData && (
          <Tooltip content={copied ? 'Copied!' : 'Copy to clipboard'} relationship="label">
            <Button
              className={styles.copyButton}
              appearance="subtle"
              size="small"
              icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
              onClick={handleCopy}
            />
          </Tooltip>
        )}
      </div>
      {isExpanded && (
        <div className={styles.dataContent}>
          {hasData ? JSON.stringify(data, null, 2) : 'No data'}
        </div>
      )}
    </div>
  );
};

/**
 * Format resolved value for display
 */
function formatResolvedValue(value: unknown): string {
  if (value === undefined || value === null) return '(no value)';
  if (typeof value === 'string') {
    // Truncate long strings
    if (value.length > 100) return value.substring(0, 100) + '...';
    return value;
  }
  if (typeof value === 'object') {
    const str = JSON.stringify(value);
    if (str.length > 100) return str.substring(0, 100) + '...';
    return str;
  }
  return String(value);
}

/**
 * RunDataPanel - Shows execution data for the current node in debug mode
 * Displays expressions with their resolved values, input, output, and properties
 */
export const RunDataPanel = ({ nodeId }: RunDataPanelProps) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<RunDataTab>('expressions');

  // Get run data from the editor run store
  const getNodeRunData = useEditorRunStore((state) => state.getNodeRunData);
  const isExecutionMode = useEditorRunStore((state) => state.isExecutionMode);

  // Get node definition from workflow store
  const getAction = useWorkflowStore((state) => state.getAction);
  const getTrigger = useWorkflowStore((state) => state.getTrigger);

  const nodeRunData = useMemo(() => {
    if (!isExecutionMode) return undefined;
    return getNodeRunData(nodeId);
  }, [isExecutionMode, getNodeRunData, nodeId]);

  // Get the node definition (action or trigger)
  const nodeDefinition = useMemo(() => {
    if (nodeId.startsWith('trigger-')) {
      const name = nodeId.replace('trigger-', '');
      return getTrigger(name);
    } else if (nodeId.startsWith('action-')) {
      const name = nodeId.replace('action-', '');
      return getAction(name);
    }
    return undefined;
  }, [nodeId, getAction, getTrigger]);

  // Extract expression mappings
  const expressionMappings = useMemo(() => {
    return extractExpressionMappings(nodeDefinition, nodeRunData?.input);
  }, [nodeDefinition, nodeRunData?.input]);

  // Filter to only expressions
  const expressionsOnly = useMemo(() => {
    return expressionMappings.filter(m => m.hasExpression);
  }, [expressionMappings]);

  // Not in execution mode
  if (!isExecutionMode) {
    return (
      <div className={styles.emptyState}>
        <Text>Not in debug mode</Text>
        <Text size={200}>Load a run to see execution data</Text>
      </div>
    );
  }

  // No data for this node
  if (!nodeRunData) {
    return (
      <div className={styles.emptyState}>
        <Text>No execution data</Text>
        <Text size={200}>This node was not executed in the loaded run</Text>
      </div>
    );
  }

  const statusBadge = getStatusBadge(nodeRunData.status);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'expressions':
        return (
          <div className={styles.dataViewer}>
            {expressionsOnly.length > 0 ? (
              <>
                <Text className={styles.expressionTitle}>Expression Evaluations</Text>
                {expressionsOnly.map((mapping, idx) => (
                  <div key={idx} className={styles.expressionRow}>
                    <div className={styles.expressionHeader}>
                      <Text className={styles.expressionKey}>{mapping.paramName}</Text>
                    </div>
                    <Text className={styles.expressionAuthoredLabel}>Authored:</Text>
                    <Text className={styles.expressionAuthored}>{mapping.authoredValue}</Text>
                    <div className={styles.expressionArrow}>
                      <ArrowRightRegular />
                      <Text className={styles.expressionResolvedLabel}>Resolved to:</Text>
                    </div>
                    <Text className={styles.expressionResolved}>
                      {formatResolvedValue(mapping.resolvedValue)}
                    </Text>
                  </div>
                ))}

                {/* Also show all parameters if there are more */}
                {expressionMappings.length > expressionsOnly.length && (
                  <div className={styles.allParamsSection}>
                    <Text className={styles.expressionTitle}>All Parameters</Text>
                    {expressionMappings.filter(m => !m.hasExpression).map((mapping, idx) => (
                      <div key={idx} className={styles.expressionRow}>
                        <Text className={styles.expressionKey}>{mapping.paramName}</Text>
                        <Text className={styles.expressionResolved}>
                          {formatResolvedValue(mapping.resolvedValue)}
                        </Text>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <Text className={styles.noExpression}>No expressions in this node</Text>
                <Text size={200}>This node uses static values only</Text>
              </div>
            )}
          </div>
        );

      case 'input':
        return (
          <div className={styles.dataViewer}>
            <CollapsibleSection title="Runtime Input" data={nodeRunData.input} />
          </div>
        );

      case 'output':
        return (
          <div className={styles.dataViewer}>
            <CollapsibleSection title="Runtime Output" data={nodeRunData.output} />
          </div>
        );

      case 'properties':
        return (
          <div className={styles.dataViewer}>
            {/* Status */}
            <div className={styles.statusSection}>
              <div className={styles.statusRow}>
                <Text className={styles.statusLabel}>Status</Text>
                <Badge appearance="filled" color={statusBadge.color}>
                  {statusBadge.label}
                </Badge>
              </div>
              <div className={styles.statusRow}>
                <Text className={styles.statusLabel}>Duration</Text>
                <Text className={styles.statusValue}>
                  {formatDuration(nodeRunData.duration)}
                </Text>
              </div>
              <div className={styles.statusRow}>
                <Text className={styles.statusLabel}>Start Time</Text>
                <Text className={styles.statusValue}>
                  {formatTime(nodeRunData.startTime)}
                </Text>
              </div>
              <div className={styles.statusRow}>
                <Text className={styles.statusLabel}>End Time</Text>
                <Text className={styles.statusValue}>
                  {formatTime(nodeRunData.endTime)}
                </Text>
              </div>
              <div className={styles.statusRow}>
                <Text className={styles.statusLabel}>Node Type</Text>
                <Text className={styles.statusValue}>{nodeRunData.nodeType}</Text>
              </div>
              {nodeRunData.iterationCount !== undefined && (
                <div className={styles.statusRow}>
                  <Text className={styles.statusLabel}>Iterations</Text>
                  <Text className={styles.statusValue}>{nodeRunData.iterationCount}</Text>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.container}>
      {/* Error display at top if failed */}
      {nodeRunData.status === 'failed' && nodeRunData.error && (
        <div className={styles.errorSection}>
          <div className={styles.errorTitle}>
            <AlertRegular />
            <Text>Error: {nodeRunData.error.code}</Text>
          </div>
          <Text className={styles.errorMessage}>{nodeRunData.error.message}</Text>
        </div>
      )}

      {/* Tabs */}
      <TabList
        className={styles.tabList}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as RunDataTab)}
        size="small"
      >
        <Tab value="expressions">
          Expressions
          {expressionsOnly.length > 0 && (
            <Badge appearance="filled" size="small" color="informative" className={styles.badgeMargin}>
              {expressionsOnly.length}
            </Badge>
          )}
        </Tab>
        <Tab value="input">Input</Tab>
        <Tab value="output">Output</Tab>
        <Tab value="properties">Properties</Tab>
      </TabList>

      {/* Content */}
      <div className={styles.content}>
        {renderTabContent()}
      </div>
    </div>
  );
};

RunDataPanel.displayName = 'RunDataPanel';
